import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { UserCreate } from "../../models/Users/UserCreate";
import agent from "../../../api/agent";
import { Company } from "../../models/Companies/Company";
import { checkTokenExpiration } from "../../../api/tokenExpirationChecking";

interface Props {
  selectedCompanyId: number | undefined;
  closeForm: () => void;
  create: (user: UserCreate) => void;
  fromUserDashboard: boolean;
}

export default function CreateUserForm({
  selectedCompanyId,
  closeForm,
  create,
  fromUserDashboard,
}: Props) {
  const [userData, setUserData] = useState<UserCreate>({
    email: "",
    userName: "",
    phoneNumber: undefined,
    role: "User",
    companyId: selectedCompanyId,
  });

  const [companyList, setCompanyList] = useState<Company[]>([]);

  // Authentication
  const [token, setToken] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          if (checkTokenExpiration(tokenFLocal)) {
            return;
          }
          setToken(tokenFLocal);
          const response = await agent.Company.list(tokenFLocal);
          setCompanyList(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create(userData);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setUserData({ ...userData, [name]: value });
  }

  function handleRoleChange(event: SelectChangeEvent<string>) {
    const { name, value } = event.target;
    if (value === "Super Admin") {
      // When role is set to Super Admin, remove companyId
      setUserData((prev) => ({
        ...prev,
        [name]: value,
        companyId: undefined,
      }));
    } else {
      setUserData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  function handleCompanyNameDropdownOnChange(event: SelectChangeEvent<number>) {
    const value = event.target.value as number;
    setUserData({ ...userData, companyId: value });
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Email"
        name="email"
        value={userData.email}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        required
      />
      
      <TextField
        label="User Name"
        name="userName"
        value={userData.userName}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        required
      />

      <TextField
        label="Phone Number"
        name="phoneNumber"
        value={userData.phoneNumber}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        type="number"
      />

      {/* dropdown for Roles */}
      <FormControl fullWidth margin="normal" required>
        <InputLabel id="status-label">Roles</InputLabel>
        <Select
          labelId="Roles-label"
          name="role"
          value={userData.role}
          onChange={handleRoleChange}
          label="Role"
        >
          {fromUserDashboard ? (
            <MenuItem value="Super Admin">Super Admin</MenuItem>
          ) : null}
          <MenuItem value="Admin">Admin</MenuItem>
          <MenuItem value="User">User</MenuItem>
        </Select>
      </FormControl>

      {/* Company Dropdown */}
      {fromUserDashboard ? (
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="company-id-label">Company Name</InputLabel>
          <Select
            labelId="company-id-label"
            name="companyId"
            value={
              userData.role === "Super Admin" ? "" : userData.companyId ?? ""
            } // Empty string if undefined
            onChange={handleCompanyNameDropdownOnChange}
            label="Company Name"
            disabled={userData.role === "Super Admin"}
          >
            {companyList.map((company) => (
              <MenuItem key={company.companyId} value={company.companyId}>
                {company.companyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained" color="primary">
          Create
        </Button>
        <Button variant="outlined" onClick={closeForm}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
